/**
 * <ManagerEmployeeOnboardingView /> — manager's view of one employee's
 * onboarding. Lets the manager see all 17 items, their evidence, and
 * mark manager-driven items complete with a confirmation step.
 *
 * Route: /admin/onboarding/:employeeId
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { CheckCircle2, FileText, FileSignature, Lock, ExternalLink, Mail, Trash2, ClipboardList, Calendar } from 'lucide-react';
import { db, auth } from '../../../firebase';
import { useToast } from '../../../components/Toast';
import {
  subscribeChecklistItems,
  subscribeProgress,
  markManagerItemComplete,
  deleteChecklistItem,
  getSignature,
  getUploadedDocument,
  resendWelcomeEmail,
  resendChecklistEmail,
  sendFirstDayWelcome,
  deleteEmployeeOnboarding,
  listActiveTemplates,
  createOnboardingForEmployee,
  listActivePolicyDocuments,
  assignPoliciesToEmployee,
} from '../services/onboardingService';
import '../Onboarding.css';

function fmtTs(v) {
  if (!v) return '—';
  const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  return isNaN(d) ? '—' : d.toLocaleString();
}

export default function ManagerEmployeeOnboardingView() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [employee, setEmployee] = useState(null);
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(null);
  const [evidence, setEvidence] = useState({});
  const [confirming, setConfirming] = useState(null); // itemId pending confirm
  const [confirmingDeleteItem, setConfirmingDeleteItem] = useState(null); // itemId pending delete confirm
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendingChecklist, setResendingChecklist] = useState(false);
  const [sendingFirstDay, setSendingFirstDay] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [applyTemplateId, setApplyTemplateId] = useState('residential_cleaner_v1');
  const [applying, setApplying] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState([]);
  const [assigningPolicies, setAssigningPolicies] = useState(false);

  useEffect(() => {
    if (!employeeId) return undefined;
    (async () => {
      const snap = await getDoc(doc(db, 'employees', employeeId));
      setEmployee(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    })();
    const unsubItems = subscribeChecklistItems(employeeId, setItems);
    const unsubProg = subscribeProgress(employeeId, setProgress);
    listActiveTemplates().then((tpls) => {
      setTemplates(tpls);
      if (tpls.length && !tpls.find((t) => t.id === 'residential_cleaner_v1')) {
        setApplyTemplateId(tpls[0].id);
      }
    }).catch(() => { /* fall back to default id */ });
    listActivePolicyDocuments()
      .then((docs) => setPolicies(docs.sort((a, b) => (a.title || '').localeCompare(b.title || ''))))
      .catch(() => { /* policies optional */ });
    return () => { unsubItems(); unsubProg(); };
  }, [employeeId]);

  const assignedPolicyIds = useMemo(
    () => new Set(items.map((i) => i.policy_document_id).filter(Boolean)),
    [items]
  );
  const assignablePolicies = useMemo(
    () => policies.filter((p) => !assignedPolicyIds.has(p.id)),
    [policies, assignedPolicyIds]
  );

  // Lazy-load evidence (signature snapshot or upload) per item.
  useEffect(() => {
    items.forEach(async (item) => {
      if (item.status !== 'completed') return;
      if (evidence[item.id]) return;
      if (item.metadata?.signature_id) {
        const sig = await getSignature(item.metadata.signature_id);
        setEvidence((e) => ({ ...e, [item.id]: { kind: 'signature', ...sig } }));
      } else if (item.metadata?.upload_id) {
        const up = await getUploadedDocument(item.metadata.upload_id);
        setEvidence((e) => ({ ...e, [item.id]: { kind: 'upload', ...up } }));
      }
    });
  }, [items, evidence]);

  const totals = useMemo(() => {
    const total = items.length;
    const completed = items.filter((i) => i.status === 'completed').length;
    return { total, completed };
  }, [items]);

  const onMarkComplete = async (itemId) => {
    try {
      await markManagerItemComplete({ itemId, completedBy: auth.currentUser?.uid });
      toast.success('Item marked complete');
    } catch (e) {
      toast.error(e.message || 'Failed to update item');
    } finally {
      setConfirming(null);
    }
  };

  const onDeleteItem = async (itemId) => {
    setDeletingItemId(itemId);
    try {
      await deleteChecklistItem(itemId);
      toast.success('Item removed from checklist');
    } catch (e) {
      toast.error(e.message || 'Failed to delete item');
    } finally {
      setDeletingItemId(null);
      setConfirmingDeleteItem(null);
    }
  };

  const onResendWelcome = async () => {
    setResending(true);
    try {
      await resendWelcomeEmail(employeeId);
      toast.success(`Password reset email sent to ${employee.email}`);
    } catch (e) {
      toast.error(e.message || 'Failed to resend welcome email');
    } finally {
      setResending(false);
    }
  };

  const onResendChecklist = async () => {
    setResendingChecklist(true);
    try {
      await resendChecklistEmail(employeeId);
      toast.success(`Checklist email sent to ${employee.email}`);
    } catch (e) {
      toast.error(e.message || 'Failed to resend checklist email');
    } finally {
      setResendingChecklist(false);
    }
  };

  const onSendFirstDayWelcome = async () => {
    setSendingFirstDay(true);
    try {
      await sendFirstDayWelcome(employeeId);
      toast.success(`First-day welcome sent to ${employee.email}`);
    } catch (e) {
      toast.error(e.message || 'Failed to send first-day welcome');
    } finally {
      setSendingFirstDay(false);
    }
  };

  const togglePolicy = (id) => {
    setSelectedPolicyIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const onAssignPolicies = async () => {
    if (selectedPolicyIds.length === 0) return;
    setAssigningPolicies(true);
    try {
      const res = await assignPoliciesToEmployee({
        employeeId,
        policyIds: selectedPolicyIds,
      });
      const createdCount = res?.created?.length ?? 0;
      const skippedCount = res?.skipped?.length ?? 0;
      if (createdCount > 0) {
        toast.success(
          `Assigned ${createdCount} polic${createdCount === 1 ? 'y' : 'ies'}` +
          (skippedCount ? ` (${skippedCount} already on file)` : '')
        );
      } else {
        toast.success('No new policies to assign');
      }
      setSelectedPolicyIds([]);
    } catch (e) {
      toast.error(e.message || 'Failed to assign policies');
    } finally {
      setAssigningPolicies(false);
    }
  };

  const onApplyTemplate = async () => {
    if (!applyTemplateId) return;
    setApplying(true);
    try {
      await createOnboardingForEmployee({ employeeId, templateId: applyTemplateId });
      toast.success('Template applied');
    } catch (e) {
      toast.error(e.message || 'Failed to apply template');
    } finally {
      setApplying(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await deleteEmployeeOnboarding(employeeId, { deleteEmployee: true });
      toast.success('Employee removed');
      navigate('/admin/onboarding');
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (!employee) {
    return <div style={{ padding: 16 }}><div className="loading-screen"><div className="spinner-large" /><p>Loading…</p></div></div>;
  }

  return (
    <div style={{ padding: 16, maxWidth: 800, margin: '0 auto' }}>
      <Link to="/admin/onboarding" className="onboarding-btn secondary" style={{ padding: '6px 10px', fontSize: '0.8rem' }}>
        ← Back to onboarding
      </Link>
      <h1 style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
        {employee.firstName} {employee.lastName}
      </h1>
      <p style={{ color: '#555' }}>
        Hire date: {employee.hire_date || employee.hireDate || '—'} · Status: {employee.status}
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0 12px' }}>
        <button
          className="onboarding-btn secondary"
          style={{ padding: '6px 10px', fontSize: '0.8rem' }}
          onClick={onResendWelcome}
          disabled={resending || !employee.email}
        >
          <Mail size={12} style={{ marginRight: 4 }} />
          {resending ? 'Sending…' : 'Resend welcome email'}
        </button>
        <button
          className="onboarding-btn secondary"
          style={{ padding: '6px 10px', fontSize: '0.8rem' }}
          onClick={onResendChecklist}
          disabled={resendingChecklist || !employee.email || items.length === 0}
          title={items.length === 0 ? 'Apply a template first' : 'Resend the checklist notification email'}
        >
          <ClipboardList size={12} style={{ marginRight: 4 }} />
          {resendingChecklist ? 'Sending…' : 'Resend checklist email'}
        </button>
        <button
          className="onboarding-btn secondary"
          style={{ padding: '6px 10px', fontSize: '0.8rem' }}
          onClick={onSendFirstDayWelcome}
          disabled={sendingFirstDay || !employee.email}
          title="Send what-to-wear and what-to-bring for the first day"
        >
          <Calendar size={12} style={{ marginRight: 4 }} />
          {sendingFirstDay ? 'Sending…' : 'Send first-day welcome'}
        </button>
        {confirmDelete ? (
          <>
            <button
              className="onboarding-btn"
              style={{ padding: '6px 10px', fontSize: '0.8rem', background: '#b91c1c' }}
              onClick={onDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Confirm delete'}
            </button>
            <button
              className="onboarding-btn secondary"
              style={{ padding: '6px 10px', fontSize: '0.8rem' }}
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            className="onboarding-btn secondary"
            style={{ padding: '6px 10px', fontSize: '0.8rem', color: '#b91c1c', borderColor: '#fecaca' }}
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 size={12} style={{ marginRight: 4 }} />
            Delete employee
          </button>
        )}
      </div>

      <div className="onboarding-progress-bar">
        <div style={{ width: `${progress?.percent_complete ?? 0}%` }} />
      </div>
      <div className="onboarding-progress-meta">
        <span>{totals.completed} of {totals.total} complete</span>
        <span>{progress?.percent_complete ?? 0}%</span>
      </div>

      {items.length === 0 && (
        <div
          className="onboarding-empty"
          style={{ marginTop: 12, padding: 16, border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 8 }}
        >
          <div style={{ marginBottom: 8, fontWeight: 600 }}>No checklist applied yet</div>
          <div style={{ color: '#555', fontSize: '0.85rem', marginBottom: 8 }}>
            Pick a template and apply it to provision this employee's onboarding checklist.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={applyTemplateId}
              onChange={(e) => setApplyTemplateId(e.target.value)}
              disabled={applying}
            >
              {templates.length === 0 && (
                <option value="residential_cleaner_v1">Residential Cleaner v1</option>
              )}
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name} ({t.version})</option>
              ))}
            </select>
            <button className="onboarding-btn" onClick={onApplyTemplate} disabled={applying || !applyTemplateId}>
              {applying ? 'Applying…' : 'Apply template'}
            </button>
          </div>
        </div>
      )}

      {assignablePolicies.length > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#fafaf7',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Assign policies for signature</div>
          <div style={{ color: '#555', fontSize: '0.8rem', marginBottom: 8 }}>
            Pick one or more policies to send to this employee. They'll appear on the employee's
            onboarding checklist for signature.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
            {assignablePolicies.map((p) => (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={selectedPolicyIds.includes(p.id)}
                  onChange={() => togglePolicy(p.id)}
                  disabled={assigningPolicies}
                />
                {p.title || p.id}
              </label>
            ))}
          </div>
          <button
            className="onboarding-btn"
            onClick={onAssignPolicies}
            disabled={assigningPolicies || selectedPolicyIds.length === 0}
          >
            <FileSignature size={12} style={{ marginRight: 4 }} />
            {assigningPolicies
              ? 'Assigning…'
              : `Assign ${selectedPolicyIds.length || ''} ${selectedPolicyIds.length === 1 ? 'policy' : 'policies'}`.trim()}
          </button>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        {items.map((item) => {
          const completed = item.status === 'completed';
          const ev = evidence[item.id];
          return (
            <div key={item.id} className={`onboarding-card ${completed ? 'completed' : ''}`}>
              <div style={{ flex: 1 }}>
                <div className="onboarding-card-title">
                  {item.driver === 'manager' && <Lock size={14} style={{ marginRight: 6 }} />}
                  {item.type === 'esign' && <FileSignature size={14} style={{ marginRight: 6 }} />}
                  {item.type === 'upload' && <FileText size={14} style={{ marginRight: 6 }} />}
                  {completed && <CheckCircle2 size={14} color="#3b6e4a" style={{ marginRight: 6 }} />}
                  {item.title}
                </div>
                <div className="onboarding-card-meta">
                  {completed ? (
                    <>
                      Completed {fmtTs(item.completed_at)}
                      {item.completed_by ? ` by ${item.completed_by}` : ''}
                    </>
                  ) : item.driver === 'manager' ? 'Manager action required' : 'Awaiting employee'}
                </div>
                {ev?.kind === 'signature' && ev.document_snapshot_url && (
                  <a className="onboarding-btn secondary" href={ev.document_snapshot_url} target="_blank" rel="noreferrer" style={{ marginTop: 8, padding: '6px 10px', fontSize: '0.8rem' }}>
                    <ExternalLink size={12} /> View signed PDF
                  </a>
                )}
                {ev?.kind === 'upload' && ev.file_url && (
                  <a className="onboarding-btn secondary" href={ev.file_url} target="_blank" rel="noreferrer" style={{ marginTop: 8, padding: '6px 10px', fontSize: '0.8rem' }}>
                    <ExternalLink size={12} /> View upload
                  </a>
                )}
              </div>
              <div className="onboarding-card-action">
                {!completed && item.driver === 'manager' && confirmingDeleteItem !== item.id && (
                  confirming === item.id ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="onboarding-btn" onClick={() => onMarkComplete(item.id)}>Confirm</button>
                      <button className="onboarding-btn secondary" onClick={() => setConfirming(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="onboarding-btn" onClick={() => setConfirming(item.id)}>
                      Mark complete
                    </button>
                  )
                )}
                {confirming !== item.id && (
                  confirmingDeleteItem === item.id ? (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button
                        className="onboarding-btn"
                        style={{ background: '#b91c1c' }}
                        onClick={() => onDeleteItem(item.id)}
                        disabled={deletingItemId === item.id}
                      >
                        {deletingItemId === item.id ? 'Deleting…' : 'Confirm delete'}
                      </button>
                      <button
                        className="onboarding-btn secondary"
                        onClick={() => setConfirmingDeleteItem(null)}
                        disabled={deletingItemId === item.id}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="onboarding-btn secondary"
                      style={{ marginTop: 6, padding: '6px 10px', fontSize: '0.8rem', color: '#b91c1c', borderColor: '#fecaca' }}
                      onClick={() => setConfirmingDeleteItem(item.id)}
                      title="Remove this item from the checklist"
                    >
                      <Trash2 size={12} style={{ marginRight: 4 }} />
                      Delete item
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16 }}>
        <Link className="onboarding-btn secondary" to={`/admin/employees/${employeeId}/onboarding-archive`}>
          View archive
        </Link>
      </div>
    </div>
  );
}
